'use client';

import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Avatar, Box, Button, Chip, Link as MuiLink, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  enrichInventoryAuditorItemFromOpenFoodFacts,
  fetchInventoryAuditorItemDetails,
  type InventoryAuditorItemRow,
} from '@/hooks/useInventoryAuditorInventory';
import { useNotification } from '@/contexts/NotificationContext';
import { useAuthData } from '@/hooks/getAuthData';
import { isElevatedRole } from '@/utils/roles';
import { inventoryAuditorCatalogOriginLabel } from '@/stack-shared/constants/inventory-auditor-catalog.constants';
import InventoryAuditorCatalogItemEditDialog from '../inventory-auditor/InventoryAuditorCatalogItemEditDialog';
import TitleBar from '../layout/TitleBar';

const InventoryAuditorItemDetailPage: React.FC = () => {
  const { itemId: itemIdParam } = useParams();
  const itemId = parseInt(String(itemIdParam || ''), 10);
  const { showError, showSuccess } = useNotification();
  const { roles } = useAuthData();
  const isAdmin = roles.some((r) => isElevatedRole(r));
  const [item, setItem] = useState<InventoryAuditorItemRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const invalidId = Number.isNaN(itemId) || itemId <= 0;

  const load = useCallback(async () => {
    if (invalidId) {
      setItem(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const it = await fetchInventoryAuditorItemDetails(itemId);
      setItem(it);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load item');
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [itemId, invalidId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const onEnrich = async () => {
    if (!item) return;
    setEnriching(true);
    try {
      const res = await enrichInventoryAuditorItemFromOpenFoodFacts(item.id);
      setItem(res.item);
      if (!res.foundOff) {
        showSuccess('Open Food Facts has no data for this UPC.');
        return;
      }
      if (res.updated) {
        showSuccess('Updated from Open Food Facts.');
      } else {
        showSuccess('Open Food Facts matches what we already have.');
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Enrich failed');
    } finally {
      setEnriching(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Item catalog', href: '/app/inventory-auditor/catalog' },
    { label: item ? item.upc : 'Detail' },
  ];

  if (invalidId) {
    return (
      <>
        <TitleBar breadcrumbs={breadcrumbs} title="Invalid item" />
        <Box sx={{ p: 2 }}>
          <Typography sx={{ mb: 1 }}>Invalid catalog item.</Typography>
          <MuiLink component={RouterLink} to="/app/inventory-auditor/catalog">
            Back to catalog
          </MuiLink>
        </Box>
      </>
    );
  }

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title={item?.upc ?? 'Catalog item'} />
      <Box sx={{ p: 2, maxWidth: 640 }}>
        {loading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : !item ? (
          <Typography color="text.secondary">Item not found.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
              <Avatar variant="rounded" src={item.imageUrl || undefined} sx={{ width: 120, height: 120 }}>
                <Inventory2Icon sx={{ fontSize: 48 }} />
              </Avatar>
              <Box>
                <Typography variant="h6">{item.name?.trim() || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  UPC: {item.upc}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  UOM: {item.uomPackType ?? '—'} · Size: {item.size ?? '—'}
                </Typography>
                {item.catalogOrigin ? (
                  <Typography variant="body2" color="text.secondary">
                    Source: {inventoryAuditorCatalogOriginLabel(item.catalogOrigin)}
                  </Typography>
                ) : null}
                {item.deleted ? (
                  <Chip label="Deleted" size="small" color="warning" sx={{ mt: 1 }} />
                ) : null}
              </Box>
            </Box>
            {item.scanContext?.trim() ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {item.scanContext.trim()}
              </Typography>
            ) : null}
            {item.description?.trim() ? (
              <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {item.description}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No description stored.
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {isAdmin ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Edit item
                </Button>
              ) : null}
              <Button
                variant="contained"
                disabled={enriching}
                onClick={() => void onEnrich()}
                sx={{ textTransform: 'none' }}
              >
                {enriching ? 'Scanning Open Food Facts…' : 'Scan with Open Food Facts'}
              </Button>
            </Box>
            <MuiLink component={RouterLink} to="/app/inventory-auditor/catalog" sx={{ display: 'block', mt: 2 }}>
              ← Back to catalog
            </MuiLink>
            <InventoryAuditorCatalogItemEditDialog
              open={editOpen}
              item={item}
              onClose={() => setEditOpen(false)}
              onSaved={(updated) => setItem(updated)}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default InventoryAuditorItemDetailPage;
